import React, { useState, useEffect, useRef } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import { createCompany } from '$/store/company/actions';
import Modal from '$/components/global/modal';
import '../index.scss';
import InputBlock from '$/components/global/inputBlock';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Navigate } from 'react-router-dom';
import BixButton from '$/components/global/bixButton';

export interface ICreateCompanyProps {
  draftIsLoading?: boolean;
  companyIsLoading?: boolean;
  error?: Bixby.Models.BixbyError;
  currentCompany?: Bixby.Models.AdminCompany;
  dataSiteProviders?: Bixby.Models.IIdentifiable[];
  industries?: Bixby.Models.IIdentifiable[];
  isCreatingCompany: boolean;
  defaultCompanyType: 'public' | 'private';
  onCloseModal: () => void;
  dispatchCreateCompany: (newCompany: Bixby.Models.CreateCompanyRequest) => Promise<void>;
}

export interface CreateCompanyValues {
  name: string;
  dataSiteProvider_Id: string;
  bixbyId: string;
  industry_Id: string;
  openFigiTicker: string;
  isSECPublic: boolean;
  publicStockTicker?: string;
  cikNumber?: string;
}

const CreateCompany: React.FC<ICreateCompanyProps> = props => {
  const {
    currentCompany,
    isCreatingCompany,
    dataSiteProviders,
    industries,
    onCloseModal,
    dispatchCreateCompany,
    companyIsLoading,
    defaultCompanyType,
  } = props;

  const [redirectUrl, setRedirectUrl] = useState('');
  useEffect(() => {
    if (currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
      setRedirectUrl(`/admin/company/${currentCompany.id}`);
    }
  }, [currentCompany]);
  const initialNewCompany: Bixby.Models.CreateCompanyRequest = {
    name: '',
    dataSiteProvider_Id: defaultCompanyType === 'public' ? Bixby.Constants.EMPTY_GUID : '',
    bixbyId: '',
    industry_Id: '',
    openFigiTicker: '',
    isSECPublic: defaultCompanyType === 'public',
    publicStockTicker: undefined,
    cikNumber: undefined,
  };

  const dateSiteProviderOptions = (Bixby.Utils.orderBy(dataSiteProviders, _ => _.name) || []).map(provider => {
    const providerTyped = provider as Bixby.Models.IIdentifiable;
    return (
      <option value={providerTyped.id} key={providerTyped.id}>
        {providerTyped.name}
      </option>
    );
  });

  const industryOptions = (Bixby.Utils.orderBy(industries, _ => _.name) || []).map(industry => {
    const industryTyped = industry as Bixby.Models.IIdentifiable;
    return (
      <option value={industryTyped.id} key={industryTyped.id}>
        {industryTyped.name}
      </option>
    );
  });

  const formikRef = useRef<FormikProps<CreateCompanyValues>>(null);

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
    onCloseModal();
  };

  const normalRequiredField = Yup.string().max(256, 'Too long').required();

  const schema = Yup.object({
    name: normalRequiredField.required('Company name is required.'),
    dataSiteProvider_Id: Yup.string().required('Data site provider is required.'),
    bixbyId: normalRequiredField.required('Bixby ID is required.').trim('Bixby ID must not have leading or trailing spaces.').strict(),
    industry_Id: Yup.string().required('Industry is required.'),
    openFigiTicker: Yup.string().required('Open FIGI Ticker is required.'),
    isSECPublic: Yup.boolean().required('Ownership is required'),
    publicStockTicker: Yup.string().when('isSECPublic', {
      is: true,
      then: schema => schema.required('Public Stock Ticker is required for public companies.'), // TODO: verify schema => schema.required is correct()
    }),
    cikNumber: Yup.string().when('isSECPublic', {
      is: true,
      then: schema => schema.required('CIK Number is required for public companies.'),
    }),
  });

  const RadioBox = ({ id, name, label, checked, ...props }: { [key: string]: any }) => (
    <div className="bix_radio_btn">
      <input type="radio" id={id} name={name} checked={checked} {...props} />
      <label htmlFor={id}>
        <span className="checked" />
        {label}
      </label>
    </div>
  );

  const createCompanyForm = (
    <Modal title="Add New Company Profile" onClose={handleCloseModal} active={isCreatingCompany}>
      <div>
        <Formik
          innerRef={formikRef}
          initialValues={initialNewCompany}
          validationSchema={schema}
          onSubmit={async (values: CreateCompanyValues) => {
            const newCompany: Bixby.Models.CreateCompanyRequest = values;
            await dispatchCreateCompany(newCompany);
            onCloseModal();
          }}
          render={({ handleSubmit, values, setFieldValue }) => (
            <form className="create-company-form" onSubmit={handleSubmit}>
              <div className="profile-input-container">
                <div>
                  <InputBlock title="Company Name">
                    <Field type="text" name="name" />
                  </InputBlock>
                  <ErrorMessage name="name">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                  <InputBlock title="Ownership">
                    <Field
                      name="isSECPublic"
                      render={({ field }: { [key: string]: any }) => {
                        return (
                          <div className="radio_button_group">
                            <RadioBox
                              {...field}
                              value={'true'}
                              onChange={() => {
                                setFieldValue('isSECPublic', true);
                                setFieldValue('dataSiteProvider_Id', Bixby.Constants.EMPTY_GUID);
                              }}
                              checked={values.isSECPublic}
                              label="Public"
                              id="public"
                            />
                            <RadioBox
                              {...field}
                              value={'false'}
                              checked={!values.isSECPublic}
                              onChange={() => {
                                setFieldValue('isSECPublic', false);
                                setFieldValue('publicStockTicker', undefined);
                                setFieldValue('dataSiteProvider_Id', undefined);
                              }}
                              label="Private"
                              id="private"
                            />
                          </div>
                        );
                      }}
                    />
                  </InputBlock>
                  <InputBlock title="Bixby ID">
                    <Field type="text" name="bixbyId" />
                  </InputBlock>
                  <ErrorMessage name="bixbyId">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                  <InputBlock title="Open FIGI Ticker">
                    <Field type="text" name="openFigiTicker" />
                  </InputBlock>
                  <ErrorMessage name="openFigiTicker">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                  {values.isSECPublic && (
                    <>
                      <InputBlock title="Public Stock Ticker">
                        <Field type="text" name="publicStockTicker" />
                      </InputBlock>
                      <ErrorMessage name="publicStockTicker">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                      <InputBlock title="CIK Number">
                        <Field type="text" name="cikNumber" />
                      </InputBlock>
                      <ErrorMessage name="cikNumber">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                    </>
                  )}
                  <InputBlock title="Data Site Provider">
                    <Field name="dataSiteProvider_Id" component="select" disabled={values.isSECPublic}>
                      <option value="" key="-1" disabled>
                        Select a provider...
                      </option>
                      {dateSiteProviderOptions.filter(_ =>
                        values.isSECPublic ? true : _.key !== Bixby.Constants.EMPTY_GUID
                      )}
                    </Field>
                  </InputBlock>
                  <ErrorMessage name="dataSiteProvider_Id">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                  <InputBlock title="Industry">
                    <Field name="industry_Id" component="select">
                      <option value="" key="-1" disabled>
                        Select an industry...
                      </option>
                      {industryOptions}
                    </Field>
                  </InputBlock>
                  <ErrorMessage name="industry_Id">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                </div>
              </div>
              <BixButton useSmall type="submit" disabled={companyIsLoading} useLoading isLoading={companyIsLoading}>
                Add to Portal
              </BixButton>
            </form>
          )}></Formik>
      </div>
    </Modal>
  );

  return redirectUrl && currentCompany && isCreatingCompany ? (
    <Navigate to={redirectUrl} />
  ) : (
    <div>{createCompanyForm}</div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    companyIsLoading: state.companyState.isLoading,
    error: state.companyState.error,
    currentCompany: state.companyState.currentCompany,
    dataSiteProviders: state.dataSiteProviderState.data,
    industries: state.industryState.data,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchCreateCompany: (newCompany: Bixby.Models.CreateCompanyRequest) => dispatch(createCompany(newCompany)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateCompany);
